import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Compact inline preview of a guest's uploaded IDs — used in the Guest
 * Database table row. Renders up to `max` tiny thumbs and a "+N" chip when
 * there are more. Blob-fetches with the app's JWT so <img src> works against
 * the auth-gated /uploads/download endpoint. Click any thumb to expand to a
 * lightbox. PDFs render as a small "PDF" tile.
 */
export default function GuestIdThumbs({ paths = [], max = 3, guestName = "" }) {
  const [urls, setUrls] = useState({});
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const controllers = [];
    (async () => {
      const next = {};
      for (const p of paths.slice(0, max)) {
        try {
          const ctl = new AbortController(); controllers.push(ctl);
          const r = await api.get("/pms/uploads/download", {
            params: { path: p }, responseType: "blob", signal: ctl.signal,
          });
          next[p] = URL.createObjectURL(r.data);
        } catch (_) { next[p] = null; }
      }
      setUrls(next);
    })();
    return () => {
      controllers.forEach((c) => c.abort());
      Object.values(urls).forEach((u) => u && URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(paths.slice(0, max))]);

  if (!paths.length) return <span className="text-slate-300 text-[10px]">—</span>;
  const shown = paths.slice(0, max);
  const extra = paths.length - shown.length;
  return (
    <>
      <div className="inline-flex items-center gap-1">
        {shown.map((p, i) => {
          const url = urls[p];
          const isPdf = /\.pdf(\?|$)/i.test(p);
          return (
            <button
              key={p + i}
              type="button"
              data-testid={`guest-id-thumb-${i}`}
              onClick={(e) => { e.stopPropagation(); if (url && !isPdf) setPreview({ url, name: `${guestName || "guest"}-id-${i + 1}` }); else if (url) window.open(url, "_blank"); }}
              className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 overflow-hidden hover:ring-2 hover:ring-[#4F46E5]/40 transition-shadow flex items-center justify-center"
              title={isPdf ? "Open PDF" : "Click to preview"}
            >
              {url ? (
                isPdf ? <FileText size={14} className="text-[#4F46E5]" /> : <img src={url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Loader2 size={10} className="animate-spin text-slate-300" />
              )}
            </button>
          );
        })}
        {extra > 0 && (
          <span data-testid="guest-id-thumb-more" className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-500 flex items-center justify-center">+{extra}</span>
        )}
      </div>
      {preview && (
        <div data-testid="guest-id-preview" onClick={() => setPreview(null)}
          className="fixed inset-0 bg-slate-900/80 z-[70] flex items-center justify-center p-6 cursor-zoom-out">
          <img src={preview.url} alt={preview.name} className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
